import { isStaleBuild, refreshStaleBuild } from '$lib/stale_build';
import { isSessionEnding } from '@df/utils';

const stringifyError = (error: unknown): string => {
  try {
    const serialized = JSON.stringify(error);
    // `{}` means an object with no enumerable properties (real content lost);
    // `undefined` means a non-serialisable value (e.g. a function or symbol).
    if (serialized === undefined || serialized === '{}') {
      return String(error);
    }
    return serialized;
  } catch {
    return String(error);
  }
};

const errorToText = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  // DOMException and other error-like objects aren't always `instanceof Error`
  // but still carry a useful `message` (and often `name`). Without this,
  // JSON.stringify below serializes them to "{}" and the real reason is lost.
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = error.message;
    const name = 'name' in error ? error.name : undefined;
    if (typeof message === 'string') {
      return typeof name === 'string' ? `${name}: ${message}` : message;
    }
  }
  return stringifyError(error);
};

const createErrorText = (error: unknown, contextMessage?: string) => {
  const errorPrefix = contextMessage ? `${contextMessage}: ` : '';

  return `${errorPrefix}${errorToText(error)}`;
};

const logError = (error: unknown, contextMessage?: string) => {
  const timestamp = new Date().toLocaleString('sv');
  const text = `${createErrorText(error, contextMessage)} - ${timestamp}`;

  // Nobody can fix a build that is no longer deployed, or a failure raised while
  // the page is already on its way to /login. Checked after reporting, so a
  // deploy that strands tabs pages once rather than never.
  if (isStaleBuild() || isSessionEnding()) {
    console.warn(text, error);
  } else {
    console.error(text, error);
  }

  void refreshStaleBuild();
};

export { logError };
